// src/pages/admin/reports/ProductWiseSalesReport.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, Package, ShoppingCart, DollarSign, BarChart3, RotateCcw, TrendingUp } from 'lucide-react';
import BaseReportPage from '../../../components/reports/BaseReportPage';
import BranchService from '../../../services/branchService';
import AuthService from '../../../services/authService';
import { formatUTCDate } from '../../../utils/dateUtils';

// ---------------------------------------------------------------------------
// Summary Cards
// ---------------------------------------------------------------------------
const SummaryCards = ({ summary }) => {
  const fmt = (val) =>
    `Rs. ${parseFloat(val || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const fmtQty = (val) =>
    parseFloat(val || 0).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });

  const cards = [
    {
      id: 'total-products',
      label: 'Total Products',
      value: summary?.totalProducts ?? '—',
      icon: Package,
      color: 'from-blue-500 to-blue-600',
      bg: 'bg-blue-50',
      text: 'text-blue-600',
    },
    {
      id: 'total-orders',
      label: 'Total Orders',
      value: summary?.totalOrders ?? '—',
      icon: ShoppingCart,
      color: 'from-orange-500 to-orange-600',
      bg: 'bg-orange-50',
      text: 'text-orange-600',
    },
    {
      id: 'total-quantity',
      label: 'Total Qty Sold',
      value: summary ? fmtQty(summary.totalQuantity) : '—',
      icon: BarChart3,
      color: 'from-purple-500 to-purple-600',
      bg: 'bg-purple-50',
      text: 'text-purple-600',
    },
    {
      id: 'total-revenue',
      label: 'Total Revenue',
      value: summary ? fmt(summary.totalRevenue) : '—',
      icon: DollarSign,
      color: 'from-green-500 to-green-600',
      bg: 'bg-green-50',
      text: 'text-green-600',
    },
    {
      id: 'returned-quantity',
      label: 'Returned Qty',
      value: summary ? fmtQty(summary.totalReturnedQty) : '—',
      icon: RotateCcw,
      color: 'from-red-400 to-red-500',
      bg: 'bg-red-50',
      text: 'text-red-500',
    },
    {
      id: 'net-revenue',
      label: 'Net Revenue',
      value: summary
        ? fmt(Math.max(0, (summary.totalRevenue || 0) - (summary.totalReturnedRev || 0)))
        : '—',
      icon: TrendingUp,
      color: 'from-teal-500 to-teal-600',
      bg: 'bg-teal-50',
      text: 'text-teal-600',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div
            key={card.id}
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col gap-2"
          >
            <div className="flex items-center gap-2">
              <div
                className={`flex-shrink-0 w-9 h-9 rounded-full ${card.bg} flex items-center justify-center`}
              >
                <Icon className={`w-5 h-5 ${card.text}`} />
              </div>
              <p className="text-xs font-medium text-gray-500 leading-tight">
                {card.label}
              </p>
            </div>
            <p className={`text-lg font-bold ${card.text} truncate`}>
              {card.value}
            </p>
            {/* Bottom accent bar */}
            <div
              className={`h-1 rounded-full bg-gradient-to-r ${card.color} opacity-60`}
            />
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Filter component
// ---------------------------------------------------------------------------
const ProductWiseSalesFilters = ({ filters, onFiltersChange, onClear }) => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  const user = AuthService.getCurrentUser();
  const isManager = user?.role === 'manager';
  const userBranchId =
    user?.selectedBranch?._id || user?.branchId || user?.branch?._id;

  // Pre-set branch for manager on mount
  useEffect(() => {
    if (isManager && userBranchId && onFiltersChange) {
      onFiltersChange({ ...filters, branch: userBranchId });
    }
  }, [isManager, userBranchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load branches
  useEffect(() => {
    const loadBranches = async () => {
      try {
        setLoading(true);
        const res = await BranchService.getAllBranches();
        let data = [];
        if (res?.success) {
          data = res.data?.branches || res.data || [];
        }
        setBranches(data.filter((b) => b.isActive));
      } catch (err) {
        console.error('❌ ProductWiseSalesReport: Error loading branches:', err);
      } finally {
        setLoading(false);
      }
    };
    loadBranches();
  }, []);

  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const handleClearFilters = () => {
    const cleared = { branch: isManager ? userBranchId : '' };
    if (onClear) onClear();
    onFiltersChange(cleared);
  };

  const hasActiveFilters =
    (!isManager && filters?.branch) || filters?.fromDate || filters?.toDate;

  return (
    <div className="bg-gray-50 p-4 rounded-lg mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Branch Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Branch
          </label>
          <div className="relative">
            {isManager ? (
              <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700 font-medium">
                {branches.find((b) => b._id === userBranchId)?.name ||
                  'My Branch'}
              </div>
            ) : (
              <>
                <select
                  value={filters?.branch || ''}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  disabled={loading}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 pr-10 appearance-none"
                >
                  <option value="">All Branches</option>
                  {branches.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </>
            )}
          </div>
          {loading && !isManager && (
            <p className="text-xs text-gray-500 mt-1">Loading branches…</p>
          )}
        </div>

        {/* From Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            From Date
          </label>
          <input
            type="date"
            value={filters?.fromDate || ''}
            onChange={(e) => handleFilterChange('fromDate', e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 text-sm"
          />
        </div>

        {/* To Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            To Date
          </label>
          <input
            type="date"
            value={filters?.toDate || ''}
            onChange={(e) => handleFilterChange('toDate', e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 text-sm"
          />
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Quantity cell renderer (shared)
// ---------------------------------------------------------------------------
const fmtQty = (value) => {
  if (value === null || value === undefined) return '0';
  return parseFloat(value).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
};

const fmtCurrency = (value) => {
  if (value === null || value === undefined) return 'Rs. 0.00';
  return `Rs. ${parseFloat(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// ---------------------------------------------------------------------------
// Report page
// ---------------------------------------------------------------------------
const ProductWiseSalesReport = () => {
  const [summary, setSummary] = useState(null);

  const columns = useMemo(
    () => [
      {
        key: 'productName',
        title: 'Product Name',
        sortable: true,
      },
      {
        key: 'category',
        title: 'Category',
        sortable: true,
      },
      {
        key: 'totalSales',
        title: 'No. of Sales',
        sortable: true,
        render: (value) =>
          value === null || value === undefined
            ? '0'
            : parseInt(value).toLocaleString(),
      },
      {
        key: 'totalQuantity',
        title: 'Sold Qty',
        sortable: true,
        render: fmtQty,
      },
      {
        key: 'returnedQuantity',
        title: 'Returned Qty',
        sortable: true,
        render: (value) => {
          const v = parseFloat(value || 0);
          if (v === 0) return <span className="text-gray-400">—</span>;
          return (
            <span className="text-red-500 font-medium">{fmtQty(v)}</span>
          );
        },
      },
      {
        key: 'netQuantity',
        title: 'Net Qty',
        sortable: true,
        render: (value) => (
          <span className="font-semibold text-teal-700">{fmtQty(value)}</span>
        ),
      },
      {
        key: 'unitPrice',
        title: 'Unit Price',
        sortable: true,
        render: fmtCurrency,
      },
      {
        key: 'totalRevenue',
        title: 'Total Revenue',
        sortable: true,
        render: fmtCurrency,
      },
      {
        key: 'returnedRevenue',
        title: 'Returned Amt',
        sortable: true,
        render: (value) => {
          const v = parseFloat(value || 0);
          if (v === 0) return <span className="text-gray-400">—</span>;
          return (
            <span className="text-red-500 font-medium">{fmtCurrency(v)}</span>
          );
        },
      },
      {
        key: 'netRevenue',
        title: 'Net Revenue',
        sortable: true,
        render: (value) => (
          <span className="font-semibold text-teal-700">{fmtCurrency(value)}</span>
        ),
      },
      {
        key: 'lastSaleDate',
        title: 'Last Sale',
        sortable: true,
        render: (value) => {
          if (!value) return '—';
          return formatUTCDate(value, 'MMM dd, yyyy');
        },
      },
    ],
    []
  );

  return (
    <div className="space-y-4">
      {/* Summary Cards — always visible, updates after data loads */}
      <SummaryCards summary={summary} />

      <BaseReportPage
        title="Product-Wise Sales Report"
        subtitle="Sales breakdown by product — quantity sold, returns, and net revenue"
        reportType="productWiseSales"
        columns={columns}
        enableFilters={true}
        customFilterComponent={ProductWiseSalesFilters}
        onDataLoaded={(result) => setSummary(result.summary || null)}
      />
    </div>
  );
};

export default ProductWiseSalesReport;
